import type { Dichtheid } from "./Gezondheidsring";

/**
 * De elf tegelontwerpen uit het ontwerp van 30-09-2026.
 *
 * <p>Een ontwerp is niet meer dan een naam, een vorm, een aanbevolen dichtheid en een lijst sleutels uit
 * de componentbibliotheek. Meer hoeft het niet te zijn: de vorm weet hoe hij eruitziet, de
 * componentbibliotheek weet wat een onderdeel betekent, en het onderdeel weet waar zijn gegevens vandaan
 * komen. Hier staat alleen welke combinatie het ontwerp voorstelt.
 *
 * <p><b>Aanbevolen en niet voorgeschreven.</b> Het ontwerp zegt dat het systeem de dichtheid bepaalt uit
 * het aantal zichtbare tegels; wat hier staat is wat een ontwerp aankan als hij de ruimte krijgt. Een
 * tegel die compact moet, wordt compact, ook als er "breed" staat.
 *
 * <p><b>Waarom een aantal ontwerpen onderdelen noemt die nog niet bestaan.</b> Gedrag en sociaal vraagt
 * om oordelen per diersoort en per situatie; het Verzorgingsprofiel om vacht, oren en nagels apart. Die
 * gegevens zitten niet in het dossier. Ze staan hier wel, want het ontwerp bestaat en de vorm werkt: een
 * onderdeel zonder gegevens zegt "nog niets geregistreerd", en dat is waar. Zodra de api ze kent, doen
 * ze het vanzelf mee -- er hoeft dan niets aan deze lijst te veranderen behalve de sleutels.
 */

export type VormSleutel =
  | "ring"
  | "halvemeter"
  | "satellieten"
  | "gesplitst"
  | "statuslijst"
  | "radiaal"
  | "prijskaart"
  | "tweekolommen"
  | "boogmeter"
  | "waardenboog"
  | "kwadranten";

export interface TegelOntwerp {
  sleutel: string;
  /** i18n-sleutel van de naam zoals het ontwerp hem noemt. */
  naam: string;
  vorm: VormSleutel;
  /** De dichtheid waar dit ontwerp voor bedoeld is; het systeem mag hem verlagen. */
  aanbevolen: Dichtheid;
  /** Sleutels uit {@link DIER_COMPONENTEN}; onbekende sleutels worden overgeslagen. */
  componenten: string[];
  /**
   * Alleen aanbieden als het pension per dier rekent.
   *
   * <p>Geldt voor de prijskaart: bij een tarief per accommodatie gaat die prijs niet over dit dier.
   */
  alleenBijTariefPerDier?: boolean;
}

export const TEGELONTWERPEN: TegelOntwerp[] = [
  {
    sleutel: "gezondheidsring",
    naam: "dierTegel.ontwerp.gezondheidsring",
    vorm: "ring",
    aanbevolen: "uitgebreid",
    componenten: ["vaccinaties", "ontworming", "ontvlooiing", "medicatie", "gewicht"],
  },
  {
    sleutel: "zorgmeter",
    naam: "dierTegel.ontwerp.zorgmeter",
    vorm: "halvemeter",
    aanbevolen: "normaal",
    // "Drinken" kent het dossier niet; voeding, medicatie en verzorging wel.
    componenten: ["voeding", "medicatie", "verzorging"],
  },
  {
    sleutel: "inEenOogopslag",
    naam: "dierTegel.ontwerp.inEenOogopslag",
    vorm: "satellieten",
    aanbevolen: "normaal",
    componenten: ["chipPaspoort", "gewicht", "voeding", "dierenarts"],
  },
  {
    sleutel: "voedingEnGewicht",
    naam: "dierTegel.ontwerp.voedingEnGewicht",
    vorm: "gesplitst",
    aanbevolen: "compact",
    componenten: ["voeding", "gewicht"],
  },
  {
    sleutel: "medischeVeiligheid",
    naam: "dierTegel.ontwerp.medischeVeiligheid",
    vorm: "statuslijst",
    aanbevolen: "uitgebreid",
    componenten: ["allergieen", "medicatie", "vaccinaties", "dierenarts"],
  },
  {
    sleutel: "gedragEnSociaal",
    naam: "dierTegel.ontwerp.gedragEnSociaal",
    vorm: "radiaal",
    aanbevolen: "normaal",
    // Oordelen per situatie (andere honden, katten, kinderen) bestaan nog niet; gedrag is wat er is.
    componenten: ["gedrag"],
  },
  {
    sleutel: "verzorgingsprofiel",
    naam: "dierTegel.ontwerp.verzorgingsprofiel",
    vorm: "ring",
    aanbevolen: "normaal",
    // Vacht, oren en nagels apart bestaan nog niet; verzorging is wat er is.
    componenten: ["verzorging"],
  },
  {
    sleutel: "tariefPerDier",
    naam: "dierTegel.ontwerp.tariefPerDier",
    vorm: "prijskaart",
    aanbevolen: "compact",
    componenten: ["dagprijs"],
    alleenBijTariefPerDier: true,
  },
  {
    sleutel: "identiteitEnGezondheid",
    naam: "dierTegel.ontwerp.identiteitEnGezondheid",
    vorm: "tweekolommen",
    aanbevolen: "uitgebreid",
    componenten: ["chipPaspoort", "vaccinaties", "medicatie", "ontworming"],
  },
  {
    sleutel: "aandachtsmeter",
    naam: "dierTegel.ontwerp.aandachtsmeter",
    vorm: "boogmeter",
    aanbevolen: "compact",
    componenten: ["allergieen"],
  },
  {
    sleutel: "inEenBoog",
    naam: "dierTegel.ontwerp.inEenBoog",
    vorm: "waardenboog",
    aanbevolen: "uitgebreid",
    componenten: ["gewicht", "voeding", "vaccinaties", "medicatie", "allergieen"],
  },
  {
    sleutel: "dierkompas",
    naam: "dierTegel.ontwerp.dierkompas",
    vorm: "kwadranten",
    aanbevolen: "uitgebreid",
    componenten: ["voeding", "vaccinaties", "gedrag", "verzorging"],
  },
];

export const ONTWERP_PER_SLEUTEL: Record<string, TegelOntwerp> =
  Object.fromEntries(TEGELONTWERPEN.map((o) => [o.sleutel, o]));

/**
 * De dichtheid die het systeem kiest bij een aantal zichtbare tegels.
 *
 * <p>Rechtstreeks uit de tabel in het ontwerp. De gebruiker kiest alleen wélke tegels hij ziet en in
 * welke volgorde; hoe breed en hoe dicht ze worden is systeemwerk. Dat is bewust: laat je de gebruiker
 * ook de maat kiezen, dan is het eerste wat er gebeurt dat alles op "uitgebreid" staat en er niets meer
 * naast elkaar past.
 */
export function dichtheidBijAantal(aantal: number): Dichtheid {
  if (aantal <= 1) {
    return "uitgebreid";
  }
  // Bij vijf of meer wordt het een compact raster; daaronder is normaal de maat, en de hoofdtegel van
  // een drietal krijgt zijn eigen behandeling in de indeling en niet hier.
  return aantal >= 5 ? "compact" : "normaal";
}
